import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  Validators,
  FormGroup,
  FormBuilder,
  ReactiveFormsModule,
  FormsModule
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerModule
} from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { I18NextModule } from 'angular-i18next';
import i18next from 'i18next';
import { catchError, filter, Observable, of, switchMap } from 'rxjs';
import { EsService } from 'src/app/core/services/es.service';
import { UserService } from 'src/app/core/services/user.service';
import { FeedbackModalComponent } from 'src/app/shared/components/feedback-modal/feedback-modal.component';
import { ModalCreateDriverComponent } from 'src/app/shared/components/modal-create-driver/modal-create-driver.component';
import { CentersES } from 'src/app/shared/model/es.model';
import { EsReasons } from 'src/app/shared/model/esRequest.enum';
import { User } from 'src/app/shared/model/user.model';

export class CustomDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1;
  }
}
@Component({
  standalone: true,
  selector: 'app-es-request-info',
  templateUrl: './es-request-info.component.html',
  styleUrls: ['./es-request-info.component.scss'],
  imports: [
    CommonModule,
    I18NextModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always' }
    },
    { provide: DateAdapter, useClass: CustomDateAdapter }
  ]
})
export class EsRequestInfoComponent implements OnInit {
  @Output() formEmitter = new EventEmitter<any>();
  @Input() user!: User;

  placeholder = i18next.t('selectReason');
  driverType = '';
  searchControl = new FormControl('', [Validators.minLength(3)]);
  searchExternalControl = new FormControl('', [Validators.minLength(3)]);
  filteredOptions: Observable<User[]> = of([]);
  externalDrivers: Observable<User[]> = of([]);
  fullNameControl = false;
  nisControl = false;
  isSelected = false;
  esReasonsEnum = EsReasons;
  esReasonsKeys: (keyof typeof EsReasons)[] = Object.keys(
    EsReasons
  ) as (keyof typeof EsReasons)[];
  centers: CentersES[] = [];
  minDate: string;
  fullName = '';
  exitDateString!: string;
  entryDateString!: string;

  /* errors */
  driverTypeError = false;
  reasonText = false;
  nisError = false;
  nisErrorLength = false;
  nameError = false;
  noNameError = false;
  errorNisName = false;
  errorSearch = false;
  nisComplete = false;
  nisOK = false;

  /* Selector de horas */
  hoursMinutes: string[] = [];
  selectedHour = '';
  actualHour!: string;
  today = new Date();

  form: FormGroup = this.fb.group({});
  driverForm: FormGroup = this.fb.group({});

  @ViewChild('exitPicker') exitPicker!: MatDatepicker<any>;
  @ViewChild('entryPicker') entryPicker!: MatDatepicker<any>;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    public dialog: MatDialog,
    private esService: EsService
  ) {
    const today = new Date();
    this.minDate = this.formatDate(today);
  }

  ngOnInit(): void {
    this.initForm();
    this.initDriverForm();
    this.esService.getCenters().subscribe({
      next: (data: CentersES[]) => {
        this.centers = data;
      },
      error: (error) => {
        console.error('Error fetching authorizers:', error);
      },
      complete: () => {
        console.log('Fetch complete');
      }
    });

    this.generateHours();
    this.updateInfo();
  }

  generateHours() {
    for (let i = 0; i < 24; i++) {
      const hours = i < 10 ? '0' + i : i.toString();
      this.hoursMinutes.push(hours + ':00');
      this.hoursMinutes.push(hours + ':30');
    }
    const actualHour = new Date().getHours();
    const actualMinutes = new Date().getMinutes();
    if (actualHour < 10 && actualMinutes < 10) {
      this.actualHour = `0${actualHour}:0${actualMinutes}`;
    } else if (actualHour < 10 && actualMinutes > 10) {
      this.actualHour = `0${actualHour}:${actualMinutes}`;
    } else if (actualHour > 10 && actualMinutes < 10) {
      this.actualHour = `${actualHour}:0${actualMinutes}`;
    } else {
      this.actualHour = `${actualHour}:${actualMinutes}`;
    }
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  changeDate(type: string) {
    if (type === 'exitDate') {
      this.form.controls['exitHour'].reset();
      this.form.controls['entryDate'].reset();
      this.form.controls['entryHour'].reset();
    } else if (type === 'exitHour' || type === 'entryDate') {
      this.form.controls['entryHour'].reset();
    }

    this.updateInfo();
  }

  getAutocomplete() {
    this.filteredOptions = this.searchControl.valueChanges.pipe(
      filter(() => !this.nisOK),
      switchMap((value) =>
        value!.length >= 3
          ? this.userService.getUsersName(value!).pipe(
              catchError(() => {
                this.isSelected = false;
                this.errorSearch = true;
                this.nameError = true;
                return of([]);
              }),
              switchMap((users) => {
                this.errorSearch = false;
                this.isSelected = false;
                if (users.length === 0 && !this.isSelected) {
                  //Si no hay usuarios que coincidan con la busqueda
                  this.nameError = true;
                  this.noNameError = false;
                  return of(users);
                } else if (
                  users.length > 0 &&
                  !this.isSelected &&
                  this.fullName !== this.searchControl.value
                ) {
                  this.noNameError = true;
                  this.nameError = false;
                  return of(users);
                } else {
                  this.nameError = false;
                  this.noNameError = false;
                  return of(users);
                }
              })
            )
          : of([])
      )
    );
  }

  getAutocompleteExternal() {
    this.externalDrivers = this.searchExternalControl.valueChanges.pipe(
      switchMap((value) =>
        value!.length >= 3
          ? this.esService.getExternalDrivers(value!).pipe(
              catchError(() => {
                this.isSelected = false;
                this.errorSearch = true;
                this.nameError = true;
                return of([]);
              }),
              switchMap((users) => {
                this.errorSearch = false;
                this.isSelected = false;
                if (users.length === 0 && !this.isSelected) {
                  //Si no hay usuarios que coincidan con la busqueda
                  this.nameError = true;
                  this.noNameError = false;
                  return of(users);
                } else if (
                  users.length > 0 &&
                  !this.isSelected &&
                  this.fullName !== this.searchControl.value
                ) {
                  this.noNameError = true;
                  this.nameError = false;
                  return of(users);
                } else {
                  this.nameError = false;
                  this.noNameError = false;
                  return of(users);
                }
              })
            )
          : of([])
      )
    );

    this.searchExternalControl.valueChanges.subscribe((value) => {
      //Para que solo salga 'errorNoNisAndNoName'
      if (value?.length === 0) {
        this.nameError = false;
      }
      this.isSelected = false;
      this.noNameError = false;
    });
  }

  initForm(): void {
    this.form = this.fb.group({
      centerEntry: ['', [Validators.required]],
      centerExit: ['', [Validators.required]],
      exitDate: ['', [Validators.required]],
      entryDate: ['', [Validators.required]],
      exitHour: ['', [Validators.required]],
      entryHour: ['', [Validators.required]],
      reason: ['', [Validators.required]],
      reasonText: ['', [Validators.required]],
      driverType: ['', [Validators.required]]
    });
  }

  initDriverForm(): void {
    this.getAutocomplete();
    this.getAutocompleteExternal();

    if (this.form.value.driverType === 'INTERNAL') {
      this.driverForm = this.fb.group({
        nis: ['', [Validators.required]],
        fullName: ['', [Validators.required]],
        driverLicense: [, [Validators.required]]
      });
      this.nisAndFullNameControl();
    } else {
      this.driverForm = this.fb.group({
        fullName: ['', [Validators.required]],
        nis: ['', [Validators.required]],
        driverLicense: [, [Validators.required]]
      });
    }
  }

  changeDriverType() {
    if (
      this.form.controls['driverType'].dirty &&
      this.form.value.driverType === 'empty'
    ) {
      this.driverTypeError = true;
    } else {
      this.driverTypeError = false;
    }
    this.initDriverForm();
    this.updateInfo();
    this.driverForm.get('fullName')?.setValue('');
    this.driverForm.get('nis')?.setValue('');
    this.nameError = false;
    this.noNameError = false;
    this.nisError = false;
    this.nisErrorLength = false;
    this.errorNisName = false;
    this.nisOK = false;
  }

  updateInfo() {
    this.exitDateString = new Date(this.form.value.exitDate).toDateString();
    this.entryDateString = new Date(this.form.value.entryDate).toDateString();
    const formValue = this.form.getRawValue();
    const driverFormValue = this.driverForm.getRawValue();
    let formObj;

    if (!this.nisErrorLength && !this.nisError) {
      formObj = {
        ...formValue,
        driver: { ...driverFormValue }
      };
    } else {
      formObj = {};
    }
    this.formEmitter.emit(formObj);
  }

  updateInfoNis() {
    this.setValidateNis();
  }

  setValidateNis() {
    const nis = this.driverForm.get('nis')?.value;
    const name = this.driverForm.get('fullName');

    this.nisError = false;
    this.nisErrorLength = false;

    let formObj;

    if (nis && nis.length < 6) {
      name?.disable({ emitEvent: false });
      this.errorNisName = false;
      this.nisErrorLength = true;
      this.nisError = false;
      formObj = {};
      this.formEmitter.emit(formObj);
    } else if (nis.length === 0) {
      name?.enable({ emitEvent: false });
      this.errorNisName = true;
    } else if (nis && nis.length >= 6) {
      // No permitir el mismo NIS que el usuario logado
      if (nis === this.user.nis) {
        this.nisError = true;
        this.nisErrorLength = false;
        this.errorNisName = false;
        this.nisOK = false;
        this.isSelected = false;

        this.driverForm.get('nis')?.setValue('', { emitEvent: false });
        this.driverForm.get('fullName')?.setValue('', { emitEvent: false });
        name?.enable({ emitEvent: false });

        this.dialog.open(FeedbackModalComponent, {
          data: {
            text: i18next.t('errorSameNisDriver'),
            close: true,
            theme: 'ligth'
          }
        });

        formObj = {};
        this.formEmitter.emit(formObj);
        return;
      }
      this.userService.getUserNisName(nis).subscribe({
        next: (resp: string) => {
          if (resp) {
            this.isSelected = true;
            this.nisOK = true;
            this.driverForm.get('fullName')?.patchValue(resp);
            this.searchControl.setValue(resp);
            this.nisError = false;
            this.nisErrorLength = false;
            this.errorNisName = false;
            const formValue = this.form.getRawValue();
            const driverFormValue = this.driverForm.getRawValue();
            formObj = {
              ...formValue,
              driver: { ...driverFormValue }
            };
            if (name?.invalid) {
              name?.disable({ emitEvent: false });
            }
          } else {
            this.nisError = true;
            this.errorNisName = false;
            formObj = {};
          }
          this.formEmitter.emit(formObj);
        },
        error: () => {
          this.nisError = true;
          this.nisErrorLength = false;
          this.errorNisName = false;
          formObj = {};
          this.formEmitter.emit(formObj);
        }
      });
    }
  }

  nisAndFullNameControl(): void {
    const nis = this.driverForm.get('nis');
    const name = this.driverForm.get('fullName');

    this.driverForm.get('nis')?.valueChanges.subscribe((value) => {
      if (value && value.length > 0 && !this.isSelected && !this.nisComplete) {
        this.setValidateNis();
        name?.disable();
        this.driverForm.get('fullName')?.patchValue('');
        this.errorNisName = false;
      } else if (!value) {
        this.nisError = false;
        this.nisErrorLength = false;
        this.errorNisName = true;
        name?.enable();
        this.nisOK = false;
        this.isSelected = false;
      }
    });

    this.searchControl.valueChanges.subscribe((value) => {
      if (value && value.length > 0 && !this.isSelected) {
        nis?.disable({ emitEvent: false });
        this.driverForm.get('nis')?.patchValue('');
        this.nisComplete = false;
        this.errorNisName = false;
      } else if (!value) {
        this.nameError = false;
        this.noNameError = false;
        this.errorNisName = true;
        this.nisComplete = false;
        nis?.enable();
        this.driverForm.get('nis')?.patchValue('');
      }
    });
  }

  onOptionSelected(event: any, type: string): void {
    this.nameError = false;
    this.isSelected = true;
    this.noNameError = false;
    const selectedUserName = event.option.value.fullName;
    const selectedUserNis = event.option.value.id;
    this.driverForm.get('fullName')?.patchValue(selectedUserName);
    this.driverForm
      .get('nis')
      ?.patchValue(selectedUserNis, { emitEvent: true });
    this.driverForm.get('nis')?.disable({ emitEvent: false });
    this.driverForm.get('fullName')?.enable();
    this.searchControl.setValue(selectedUserName);
    this.nisAndFullNameControl();
    this.fullName = selectedUserName;
    this.nisComplete = true;
    if (type === 'INTERNAL') {
      this.setValidateNis();
    }
    this.updateInfo();
  }

  createExternalDriver() {
    this.dialog.open(ModalCreateDriverComponent);
  }

  modifyExternalDriver() {
    const nis = this.driverForm.get('nis')?.value;
    this.esService.getExternalDriver(nis).subscribe({
      next: (resp) => {
        const user = resp;
        this.dialog.open(ModalCreateDriverComponent, {
          data: user
        });
      },
      error: () => {
        this.dialog.open(FeedbackModalComponent, {
          data: {
            text: i18next.t('errorGetUser'),
            close: true,
            theme: 'ligth'
          }
        });
      }
    });
  }

  changeReason(reason: string) {
    this.form.get('reasonText')?.reset();
    if (reason === '') {
      this.placeholder = i18next.t('loanSelectReason');
    } else if (reason === 'MALFUNCTION_MAINTENANCE_ACCIDENT') {
      this.placeholder = i18next.t('loanPlatenumber');
    } else if (reason === 'BUSINESS_TRIP') {
      this.placeholder = i18next.t('loanDestination');
    } else {
      this.placeholder = i18next.t('loanReason');
    }
    this.validateReasonText();
    this.updateInfo();
  }
  changeReasonText() {
    if (
      this.form.controls['reasonText'].dirty &&
      this.form.value.reason !== this.esReasonsEnum.SUPPLIER_VISIT &&
      this.form.value.reasonText === ''
    ) {
      this.reasonText = true;
    } else {
      this.reasonText = false;
    }
    this.validateReasonText();
    this.updateInfo();
  }

  validateReasonText() {
    if (
      this.form.value.reason === 'SUPPLIER_VISIT' &&
      this.form.value.reasonText === null
    ) {
      this.form.get('reasonText')?.clearValidators();
      this.form.get('reasonText')?.updateValueAndValidity();
      this.reasonText = false;
    } else if (
      this.form.value.reason !== 'SUPPLIER_VISIT' &&
      this.form.value.reason !== '' &&
      !this.form.value.reasonText
    ) {
      this.reasonText = true;
    } else {
      this.reasonText = false;
    }
    this.updateInfo();
  }

  close() {
    this.exitPicker['_destroyOverlay']();
    this.entryPicker['_destroyOverlay']();
  }
}
